// PrimitiveTypeChips — top-of-calendar quick filter for the FOUR primitive
// types every event in /calendar-items can be:  event · activity · chore · todo.
//
// Selection model: multi-toggle. All-on by default; clicking a chip toggles
// it off. State persists per-family in localStorage.
//
// Why this exists: every Batch 38 review promised "filter chores/todos/
// events/activities" at the top of the main calendar, but only the
// sub-calendar (kind) chips shipped. This component closes that gap.
import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";

const TYPES = [
  // testid kebab, label, predicate against an item shape
  { id: "event",    label: "Events",     match: (it) => it.type === "event" && !it.activity_id },
  { id: "activity", label: "Activities", match: (it) => it.type === "event" && !!it.activity_id },
  { id: "study",    label: "Study",      match: (it) => it.type === "event" && (it.kind_label === "study" || it.session_kind === "study") },
  { id: "chore",    label: "Chores",     match: (it) => it.type === "chore" || it.kind === "chore" },
  { id: "todo",     label: "Todos",      match: (it) => it.type === "todo"  || it.kind === "todo" },
  { id: "poll",     label: "Polls",      match: (it) => it.type === "poll"  || it.kind === "poll" },
];

const ALL_IDS = TYPES.map((t) => t.id);

export function PrimitiveTypeChips({ onChange }) {
  const { family } = useAuth();
  const storageKey = family?.id ? `kinly:cal-types:${family.id}` : null;
  const [enabled, setEnabled] = useState(() => new Set(ALL_IDS));

  // Hydrate from localStorage once we know the family.
  useEffect(() => {
    if (!storageKey) return;
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) setEnabled(new Set(arr));
      }
    } catch { /* defaults remain */ }
  }, [storageKey]);

  // Re-emit to parent on every change (including the initial mount, so the
  // first render filters properly even if all types are enabled).
  useEffect(() => {
    onChange?.([...enabled]);
  }, [enabled, onChange]);

  const toggle = (id) => {
    setEnabled((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      if (storageKey) localStorage.setItem(storageKey, JSON.stringify([...next]));
      return next;
    });
  };

  const allOn = enabled.size === TYPES.length;
  const resetAll = () => {
    const next = new Set(ALL_IDS);
    setEnabled(next);
    if (storageKey) localStorage.setItem(storageKey, JSON.stringify([...next]));
  };

  return (
    <div
      data-testid="calendar-primitive-chips"
      className="flex gap-1.5 overflow-x-auto -mx-4 px-4 scrollbar-hide"
    >
      <button
        type="button"
        onClick={resetAll}
        data-testid="cal-type-all"
        className={[
          "shrink-0 h-8 px-3 rounded-full text-[12px] font-medium tap border transition-colors",
          allOn
            ? "bg-foreground text-background border-foreground"
            : "bg-card text-foreground border-border hover:border-foreground/40",
        ].join(" ")}
        aria-pressed={allOn}
      >
        All
      </button>
      {TYPES.map((t) => {
        const on = enabled.has(t.id);
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => toggle(t.id)}
            data-testid={`cal-type-${t.id}`}
            className={[
              "shrink-0 h-8 px-3 rounded-full text-[12px] font-medium tap border transition-colors",
              on
                ? "bg-primary/10 text-primary border-primary/30"
                : "bg-card text-muted-foreground border-border line-through opacity-60",
            ].join(" ")}
            aria-pressed={on}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

// Exposed for the calendar view to apply the same predicate set without
// recomputing the type definitions.
export function filterByEnabledTypes(items, enabledIds) {
  if (!enabledIds || enabledIds.length === TYPES.length) return items;
  const enabled = new Set(enabledIds);
  return items.filter((it) => {
    for (const t of TYPES) {
      if (enabled.has(t.id) && t.match(it)) return true;
    }
    return false;
  });
}
